import { useCallback, useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { useMutation } from '@tanstack/react-query';
import { PaperAirplaneIcon } from '@heroicons/react/24/outline';
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  EnvironmentBadge,
  Spinner,
  TabPanel,
  Tabs,
  TextAreaField,
  useToast,
} from '@csa/ui';
import { api, ApiError } from '../../lib/api.js';
import { useJobStream } from '../../lib/useJobStream.js';
import { TargetPicker, useTargets } from '../../components/TargetPicker.js';
import { JustificationField, isJustified } from '../../components/JustificationField.js';
import {
  SubmitOutcome,
  SubmitStatusChip,
  type SubmitResponse,
} from '../../components/SubmitOutcome.js';
import { KeyValueEditor, toRecord, type KeyValueRow } from './KeyValueEditor.js';
import { SecretBindingPicker, type SecretRef } from './SecretBindingPicker.js';
import { ResponseViewer, type ApiResult } from './ResponseViewer.js';

/**
 * The API console.
 *
 * Postman-shaped, with one structural difference that is the whole point:
 * **the user never supplies a URL.** They pick a target and supply a path.
 * The base URL comes from the target's configuration, the path is checked
 * against that target's allowlist, and the connector resolves the address
 * itself with an SSRF guard installed in the DNS lookup.
 *
 * A free-text URL field would make every one of those defences advisory.
 * It is the difference between "a tool that can call approved endpoints"
 * and "a general-purpose HTTP client running inside the VPC holding
 * production credentials".
 *
 * The second difference: credentials are references, never values. A user
 * picks "the production API key"; the worker resolves it from Secrets
 * Manager at execution and the value never reaches the browser.
 */

const SAFE_METHODS: ReadonlySet<string> = new Set(['GET', 'HEAD', 'OPTIONS']);

/**
 * Headers the server refuses.
 *
 * `Host` and the `X-Forwarded-*` family would let a caller misrepresent the
 * request's origin to the target -- forging the source address that the
 * *downstream* system audits. `Authorization` and `Cookie` would let a user
 * hand-craft a credential rather than referencing a managed one, which is
 * the entire control. The server enforces this independently; the list is
 * duplicated here only so the refusal is not a surprise.
 */
const RESERVED_HEADERS = [
  'host',
  'authorization',
  'cookie',
  'proxy-authorization',
  'content-length',
  'connection',
  'transfer-encoding',
  'upgrade',
  'expect',
  'te',
  'keep-alive',
  'x-forwarded-for',
  'x-forwarded-host',
  'x-forwarded-proto',
];

export function ApiConsole(): ReactElement {
  const { toast } = useToast();
  const { targets, isPending } = useTargets('rest');

  const [targetId, setTargetId] = useState<string | null>(null);
  const [method, setMethod] = useState('GET');
  const [path, setPath] = useState('/');
  const [queryRows, setQueryRows] = useState<KeyValueRow[]>([]);
  const [headerRows, setHeaderRows] = useState<KeyValueRow[]>([]);
  const [body, setBody] = useState('');
  const [secretRefs, setSecretRefs] = useState<SecretRef[]>([]);
  const [justification, setJustification] = useState('');
  const [tab, setTab] = useState('query');
  const [outcome, setOutcome] = useState<SubmitResponse | null>(null);
  const [response, setResponse] = useState<ApiResult | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const target = useMemo(() => targets.find((t) => t.id === targetId) ?? null, [targets, targetId]);

  useEffect(() => {
    if (targetId === null && targets.length > 0) setTargetId(targets[0]!.id);
  }, [targets, targetId]);

  /**
   * The method list comes from the target, not from a constant. A target
   * configured read-only has no POST among its allowed methods, and
   * offering one that will be refused teaches people the tool is arbitrary.
   */
  const methods = useMemo(() => {
    const allowed = target?.rest?.allowedMethods ?? ['GET'];
    return allowed.filter((m) => SAFE_METHODS.has(m) || target?.capabilities.apiMutate);
  }, [target]);

  useEffect(() => {
    if (!methods.includes(method)) setMethod(methods[0] ?? 'GET');
  }, [methods, method]);

  const nextIdempotencyKey = useIdempotencyKey(
    [targetId, method, path, JSON.stringify(queryRows), JSON.stringify(headerRows), body].join('|'),
  );

  /**
   * The same two guards as the SQL console, for the same reasons: a click
   * while a request is in flight must not submit twice, a click after one
   * finished is a deliberate re-send and needs its own job, and the
   * displayed response must not be discarded when the server answers with
   * the job that produced it.
   */
  const finishedRef = useRef(false);
  const shownJobRef = useRef<string | null>(null);

  const submit = useMutation({
    mutationFn: (): Promise<SubmitResponse> => {
      const resend = finishedRef.current;
      finishedRef.current = false;

      return api.post<SubmitResponse>('/api/requests', {
        submission: {
          kind: 'api',
          targetId,
          method,
          path,
          query: toRecord(queryRows),
          headers: toRecord(headerRows),
          body: body.trim().length > 0 ? body : null,
          secretRefs,
          mode: 'interactive',
          justification,
        },
        idempotencyKey: nextIdempotencyKey(resend),
      });
    },

    onSuccess: (result) => {
      setOutcome(result);
      if (shownJobRef.current !== result.jobId) setResponse(null);
      setDetailOpen(result.requiresApproval);
      if (result.requiresApproval) {
        toast({
          tone: 'info',
          title: 'Sent for approval',
          message: `${result.requiredApprovals} approver(s) needed. ${result.reason}`,
        });
      }
    },
  });

  const stream = useJobStream(outcome?.jobId ?? null, {
    enabled: outcome !== null && !outcome.requiresApproval,
    onDone: useCallback(async (jobId: string, status: string) => {
      finishedRef.current = true;
      if (status !== 'succeeded') return;
      try {
        const page = await api.get<ResultPage>(`/api/jobs/${jobId}/result`);
        setResponse(firstRowAsResponse(page));
        shownJobRef.current = jobId;
      } catch {
        // The job detail already carries the status and any error, so a
        // failed result fetch degrades to less detail rather than to a
        // misleading error about the request itself.
      }
    }, []),
  });

  /** Opens itself when the detail is the point; see the SQL console. */
  useEffect(() => {
    if (stream.status === 'failed' || stream.status === 'rejected' || stream.status === 'expired') {
      setDetailOpen(true);
    }
  }, [stream.status]);

  if (isPending) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner label="Loading targets" />
      </div>
    );
  }

  if (targets.length === 0) {
    return (
      <EmptyState
        title="You have no API targets"
        description="Ask an administrator to grant your role access to an API target."
      />
    );
  }

  const mutating = !SAFE_METHODS.has(method);
  const canSend = target !== null && path.startsWith('/') && isJustified(justification);
  const sending = submit.isPending || stream.status === 'running' || stream.status === 'queued';
  const submitError = submit.error instanceof ApiError ? submit.error : null;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      <header className="flex flex-wrap items-end gap-3">
        <TargetPicker
          targets={targets}
          value={targetId}
          onChange={(next) => {
            setTargetId(next);
            // Credentials and headers are target-specific. Carrying them
            // across a switch would send one system's credential to
            // another, which is the mistake this console must not make
            // easy to commit by accident.
            setSecretRefs([]);
            setHeaderRows([]);
            setResponse(null);
            setOutcome(null);
          }}
        />
        {target ? <EnvironmentBadge environment={target.environment} /> : null}
        {target?.rest ? (
          <Badge tone="neutral" title="Per-second rate limit applied by the queue">
            {target.rest.rateLimitPerSecond}/s
          </Badge>
        ) : null}

        {outcome ? (
          <SubmitStatusChip
            outcome={outcome}
            stream={stream}
            expanded={detailOpen}
            onToggle={() => setDetailOpen((previous) => !previous)}
            controls="api-outcome-detail"
          />
        ) : null}
      </header>

      {/* The request line. The base URL is static text beside the path
          input: visible, so the user can see exactly where the call goes,
          and not editable, so they cannot change it. */}
      <Card className="flex flex-wrap items-stretch gap-2 p-2">
        <select
          className="w-32 rounded-md border border-line bg-surface px-2 py-1.5 font-mono text-sm text-body focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/40"
          value={method}
          onChange={(event) => setMethod(event.target.value)}
          aria-label="HTTP method"
        >
          {methods.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>

        <div className="flex min-w-72 flex-1 items-center overflow-hidden rounded-md border border-line bg-sunken focus-within:border-brand focus-within:ring-2 focus-within:ring-brand/40">
          <span
            className="shrink-0 px-2 py-1.5 font-mono text-xs text-subtle"
            title="Set by the target configuration and cannot be changed here"
          >
            {target?.rest?.baseUrl ?? ''}
          </span>
          <input
            className="min-w-0 flex-1 bg-transparent px-1 py-1.5 font-mono text-sm text-body outline-none"
            value={path}
            onChange={(event) => setPath(normalisePath(event.target.value))}
            placeholder="/v1/orders/123"
            aria-label="Request path"
            spellCheck={false}
          />
        </div>

        <Button
          onClick={() => submit.mutate()}
          disabled={!canSend || sending}
          leadingIcon={<PaperAirplaneIcon className="size-4" aria-hidden />}
        >
          {sending ? 'Sending…' : 'Send'}
        </Button>
      </Card>

      {target ? (
        <PathHint allowlist={target.rest?.pathAllowlist ?? []} path={path} name={target.name} />
      ) : null}

      {mutating ? (
        <Alert tone="warning">
          {method} changes state on the target
          {target?.environment === 'prod' ? ' in production' : ''}, so it needs approval before it
          runs.
        </Alert>
      ) : null}

      <Tabs
        aria-label="Request parts"
        value={tab}
        onChange={setTab}
        items={[
          { value: 'query', label: `Query${queryRows.length ? ` (${queryRows.length})` : ''}` },
          {
            value: 'headers',
            label: `Headers${headerRows.length ? ` (${headerRows.length})` : ''}`,
          },
          {
            value: 'auth',
            label: `Credentials${secretRefs.length ? ` (${secretRefs.length})` : ''}`,
          },
          { value: 'body', label: 'Body' },
        ]}
      />

      <TabPanel value="query" active={tab}>
        <KeyValueEditor rows={queryRows} onChange={setQueryRows} keyLabel="Parameter" />
      </TabPanel>

      <TabPanel value="headers" active={tab}>
        <KeyValueEditor
          rows={headerRows}
          onChange={setHeaderRows}
          keyLabel="Header"
          reservedKeys={RESERVED_HEADERS}
        />
      </TabPanel>

      <TabPanel value="auth" active={tab}>
        <SecretBindingPicker targetId={targetId} value={secretRefs} onChange={setSecretRefs} />
      </TabPanel>

      <TabPanel value="body" active={tab}>
        <TextAreaField
          label="Request body"
          hint="Sent exactly as typed."
          className="h-48 font-mono text-xs"
          value={body}
          onChange={(event) => setBody(event.target.value)}
          disabled={SAFE_METHODS.has(method)}
          placeholder={SAFE_METHODS.has(method) ? `${method} requests do not carry a body` : '{}'}
          spellCheck={false}
        />
      </TabPanel>

      <JustificationField value={justification} onChange={setJustification} />

      {submitError ? (
        <Alert tone="danger" title="Could not send">
          {submitError.message}
        </Alert>
      ) : null}

      {outcome && detailOpen ? (
        <div id="api-outcome-detail">
          <SubmitOutcome outcome={outcome} stream={stream} />
        </div>
      ) : null}
      {response ? <ResponseViewer result={response} /> : null}
    </div>
  );
}

/**
 * Tells the user whether the path they are typing is permitted.
 *
 * Advisory only -- the server checks independently, on segment boundaries.
 * The value is in the feedback loop: finding out an endpoint is not
 * allowlisted *while typing* is a different experience from finding out
 * after an approver has spent attention on the request.
 */
function PathHint({
  allowlist,
  path,
  name,
}: {
  allowlist: readonly string[];
  path: string;
  name: string;
}): ReactElement | null {
  if (allowlist.length === 0) {
    return (
      <Alert tone="warning">
        No paths are allowlisted on this target, so nothing can be called yet. An administrator has
        to add them.
      </Alert>
    );
  }

  // Segment-boundary match, the same rule the server applies: `/v1/orders`
  // must not permit `/v1/orders-internal`.
  const permitted = allowlist.some(
    (prefix) => path === prefix || path.startsWith(prefix.endsWith('/') ? prefix : `${prefix}/`),
  );
  if (permitted) return null;

  return (
    <p className="text-xs text-warning-fg">
      This path is not on the allowlist for {name}. Permitted prefixes:{' '}
      {allowlist.map((prefix) => (
        <code key={prefix} className="mr-1 font-mono">
          {prefix}
        </code>
      ))}
    </p>
  );
}

/**
 * Keeps the path a path.
 *
 * A leading slash is forced, and a pasted absolute URL is reduced to its
 * path and query. The server refuses an absolute URL outright; correcting
 * it here means someone pasting from a browser address bar gets the useful
 * part rather than a validation error.
 */
function normalisePath(raw: string): string {
  const value = raw.trim();
  if (value.startsWith('http://') || value.startsWith('https://')) {
    try {
      const url = new URL(value);
      return `${url.pathname}${url.search}`;
    } catch {
      return '/';
    }
  }
  return value.startsWith('/') ? value : `/${value}`;
}

/** The inline result endpoint's shape; only the first row matters here. */
interface ResultPage {
  readonly results: readonly { readonly rows: readonly (readonly unknown[])[] }[];
}

/**
 * An API job stores its response the same way a query stores rows: as one
 * row in the inline result. Unwrapping it here keeps the storage format
 * uniform -- one result shape for both kinds of job -- rather than adding a
 * second table for a single row.
 *
 * Always one result set: only a SQL script produces several, and an API
 * request is one request.
 */
function firstRowAsResponse(page: ResultPage): ApiResult {
  const first = page.results[0]?.rows[0]?.[0];
  if (typeof first === 'string') {
    try {
      return JSON.parse(first) as ApiResult;
    } catch {
      return { body: first };
    }
  }
  return (first ?? {}) as ApiResult;
}

/**
 * A key that changes when the request changes, and not otherwise.
 *
 * Stable across clicks so a double-click on a slow network is deduplicated
 * by the server into one job, and freshly generated whenever any part of
 * the request is edited so a genuine re-send is genuinely new. The
 * render-phase adjustment is React's supported pattern for state that must
 * change in lockstep with a prop.
 */
/** Resolved at submit time; see the SQL console for the reasoning. */
function useIdempotencyKey(signature: string): (resend: boolean) => string {
  const entry = useRef({ signature, key: crypto.randomUUID() });

  return useCallback(
    (resend: boolean) => {
      if (resend || entry.current.signature !== signature) {
        entry.current = { signature, key: crypto.randomUUID() };
      }
      return entry.current.key;
    },
    [signature],
  );
}
